import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { APP_TITLE_SUFFIX, LOGO_URL } from '@/constants/appMeta';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import api from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useStats } from '@/contexts/StatsContext';
import ChatBubble from '@/components/ChatBubble';
import SearchModal from '@/components/SearchModal';
import {
  LayoutDashboard, Gauge, ClipboardList, PackageCheck, FlaskConical, CheckCircle, Send,
  FileText, Building, Tag, Ruler, Thermometer, Users, Settings,
  LogOut, ChevronLeft, ChevronRight, ChevronDown, Sun, Moon, Bell, AlertTriangle, Info, XCircle, Building2, MessageCircle, Search,
  UserCircle, X
} from 'lucide-react';

// Navigace je sdružená do sekcí (Workflow / Číselníky / Administrace).
// `labelKey` je klíč v i18n — překládá se za běhu přes t().
// badgeKey odkazuje na pole ve `stats` z `/dashboard/stats`.
const NAV_STRUCTURE = [
  { type: 'item', path: '/', labelKey: 'menu.dashboard', icon: LayoutDashboard },
  { type: 'group', id: 'workflow', labelKey: 'sidebar.group-workflow', items: [
    { path: '/orders', labelKey: 'menu.orders', icon: ClipboardList, badgeKey: 'ordersConfirmed', badgeColor: 'var(--ct-warning)', badgeTitleKey: 'sidebar.badge-orders-confirmed' },
    { path: '/intakes', labelKey: 'menu.intakes', icon: PackageCheck, badgeKey: 'intakesNotSent', badgeColor: 'var(--ct-warning)', badgeTitleKey: 'sidebar.badge-intakes-not-sent' },
    { path: '/calibrations', labelKey: 'menu.calibrations', icon: FlaskConical, badgeKey: 'calibrationsPendingIntake', badgeColor: 'var(--ct-info)', badgeTitleKey: 'sidebar.badge-calibrations-pending-intake' },
    { path: '/approvals', labelKey: 'menu.approvals', icon: CheckCircle, badgeKey: 'calibrationsPending', badgeColor: 'var(--ct-warning)', badgeTitleKey: 'sidebar.badge-calibrations-pending' },
    { path: '/dispatches', labelKey: 'menu.dispatches', icon: Send },
    { path: '/certificates', labelKey: 'menu.certificates', icon: FileText },
  ]},
  { type: 'group', id: 'reference', labelKey: 'sidebar.group-reference', items: [
    { path: '/instruments', labelKey: 'menu.instruments', icon: Gauge },
    { path: '/instrument-types', labelKey: 'menu.instrument-types', icon: Tag },
    { path: '/companies', labelKey: 'menu.companies', icon: Building },
    { path: '/etalons', labelKey: 'menu.etalons', icon: Ruler },
    { path: '/environment', labelKey: 'menu.environment', icon: Thermometer },
  ]},
  { type: 'group', id: 'admin', labelKey: 'sidebar.group-admin', items: [
    { path: '/users', labelKey: 'menu.users', icon: Users, adminOnly: true, badgeKey: 'pendingCustomerRegistrations', badgeColor: 'var(--ct-error)', badgeTitleKey: 'sidebar.badge-pending-registrations' },
    { path: '/methodology', labelKey: 'menu.methodology', icon: Settings, adminOnly: true },
    { path: '/laboratories', labelKey: 'menu.laboratories', icon: FlaskConical, adminOnly: true },
    { path: '/lab-settings', labelKey: 'menu.lab-settings', icon: Building2, adminOnly: true },
  ]},
  { type: 'item', path: '/chat', labelKey: 'menu.chat', icon: MessageCircle },
];

const flattenNav = (structure) => structure.flatMap(e => e.type === 'group' ? e.items : [e]);
const ALL_ITEMS = flattenNav(NAV_STRUCTURE);

const GROUPS_STORAGE_KEY = 'ct-sidebar-groups-collapsed';

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const { stats } = useStats();
  const [searchOpen, setSearchOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const stored = localStorage.getItem(GROUPS_STORAGE_KEY);
      return new Set(stored ? JSON.parse(stored) : []);
    } catch { return new Set(); }
  });

  const isAdmin = user?.isAdmin;
  const visibleItems = ALL_ITEMS.filter(item => !item.adminOnly || isAdmin);

  const toggleGroup = (id) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try { localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  // Stats polling řeší StatsProvider — tady jen notifikace (bell ikona).
  // Polling zachován jako fallback (60s), ale WS dělá většinu
  // práce: real-time refresh při notification.new event + sonner toast preview.
  const fetchNotifications = useCallback(() => {
    api.get('/notifications').then(r => setNotifications(r.data)).catch(() => {});
  }, []);

  // Smazání upozornění. Perzistovaná (source: "stored") se maže na backendu;
  // heuristická (etalon-warnings apod.) DB záznam nemají — ty jen skryjeme lokálně.
  const deleteNotification = useCallback((n) => {
    setNotifications(prev => prev.filter(x => x.id !== n.id));
    if (n.source === 'stored') {
      api.delete(`/notifications/${n.id}`).catch(() => fetchNotifications());
    }
  }, [fetchNotifications]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // WS subscribe na user-specific notification topic.
  // Backend `_persist_for_all` v notification_triggers.py publishuje
  // `notification.new` event po každém create_notification (ws_publishers).
  useWebSocket(
    user?.id ? `user:${user.id}:notifications` : null,
    (event) => {
      if (event.event !== 'notification.new') return;
      // 1) Refetch — drží stored notifications synced.
      fetchNotifications();
      // 2) Sonner toast preview (jen pokud bell zavřený, jinak by user už viděl).
      if (!showNotifications) {
        const data = event.data;
        const title = (data && typeof data === 'object' && 'title' in data) ? String(data.title) : t('topbar.new-notification');
        const message = (data && typeof data === 'object' && 'message' in data) ? String(data.message) : '';
        if (message) {
          toast(title, { description: message });
        } else {
          toast(title);
        }
      }
    },
    !!user?.id
  );

  // Globální ⌘K / Ctrl+K hotkey pro universal search.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (location.pathname === '/') {
      document.title = `${t('menu.dashboard')} | ${APP_TITLE_SUFFIX}`;
      return;
    }
    const sorted = [...visibleItems].sort((a, b) => b.path.length - a.path.length);
    const match = sorted.find(
      (i) => location.pathname === i.path || location.pathname.startsWith(`${i.path}/`),
    );
    document.title = match ? `${t(match.labelKey)} | ${APP_TITLE_SUFFIX}` : APP_TITLE_SUFFIX;
  }, [location.pathname, visibleItems, t]);

  // Najít aktuální nav položku + její grupu (pokud patří do nějaké) pro breadcrumb.
  const matchedItem = [...visibleItems]
    .sort((a, b) => b.path.length - a.path.length)
    .find(i => location.pathname === i.path || location.pathname.startsWith(`${i.path}/`));
  const matchedGroup = matchedItem
    ? NAV_STRUCTURE.find(e => e.type === 'group' && e.items.some(i => i.path === matchedItem.path))
    : null;

  const errorCount = notifications.filter(n => n.type === 'error').length;
  const warnCount = notifications.filter(n => n.type === 'warning').length;
  const totalBadge = errorCount + warnCount + notifications.filter(n => n.type === 'info').length;

  const getNotifIcon = (type) => {
    if (type === 'error') return <XCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ct-error)' }} />;
    if (type === 'warning') return <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ct-warning)' }} />;
    return <Info className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ct-accent)' }} />;
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = location.pathname === item.path;
    const badgeCount = item.badgeKey ? (stats[item.badgeKey] || 0) : 0;
    const label = t(item.labelKey);
    const badgeTitle = item.badgeTitleKey ? t(item.badgeTitleKey) : '';
    return (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>
          <button
            data-testid={`nav-${item.path === '/' ? 'dashboard' : item.path.slice(1)}`}
            onClick={() => navigate(item.path)}
            className={`sidebar-nav-item w-full text-left ${collapsed ? 'collapsed-item' : ''} ${active ? 'active' : ''}`}
          >
            <Icon className="icon flex-shrink-0" />
            {!collapsed && <span className="truncate flex-1">{label}</span>}
            {badgeCount > 0 && (
              <span
                className={`text-[10px] font-bold rounded flex-shrink-0 ${collapsed ? 'absolute -top-0.5 -right-0.5 min-w-[16px] px-1 py-0 text-center' : 'ml-auto px-1.5 py-0.5'}`}
                style={{ background: item.badgeColor || 'var(--ct-accent)', color: '#fff', lineHeight: '1.4', boxShadow: collapsed ? '0 0 0 2px var(--ct-surface)' : undefined }}
                title={badgeTitle}
              >
                {badgeCount}
              </span>
            )}
          </button>
        </TooltipTrigger>
        {collapsed && (
          <TooltipContent side="right" className="text-xs">
            {label}
            {badgeCount > 0 && ` — ${badgeCount} ${badgeTitle ? `(${badgeTitle})` : ''}`}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--ct-bg)' }}>
      {/* Sidebar */}
      <div
        className="flex flex-col border-r transition-all duration-200 flex-shrink-0"
        style={{
          width: collapsed ? '64px' : '260px',
          background: 'var(--ct-bg-secondary)',
          borderColor: 'var(--ct-border)',
        }}
      >
        {/* Logo / Toggle */}
        <div className="flex items-center h-14 px-3 border-b gap-2" style={{ borderColor: 'var(--ct-border)' }}>
          {!collapsed && (
            <button
              data-testid="sidebar-logo"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 rounded px-1 py-0.5 -mx-1 hover:opacity-80 transition-opacity"
              title={t('sidebar.go-to-dashboard')}
              aria-label={t('sidebar.go-to-dashboard')}
            >
              <img src={LOGO_URL} alt="Logo" className="w-7 h-7 flex-shrink-0 rounded" />
              <span className="text-base font-bold tracking-tight" style={{ fontFamily: 'Inter', color: 'var(--ct-accent)' }}>
                CalibraTech 2.0
              </span>
            </button>
          )}
          <button
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
            className={`${collapsed ? 'mx-auto' : 'ml-auto'} p-1.5 rounded transition-colors`}
            style={{ color: 'var(--ct-accent)', background: collapsed ? 'rgba(var(--ct-accent-rgb), 0.1)' : 'transparent' }}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav — grouped */}
        <ScrollArea className="flex-1 py-2 px-2">
          <TooltipProvider delayDuration={0}>
            <nav className="space-y-0.5">
              {NAV_STRUCTURE.map((entry, idx) => {
                if (entry.type === 'item') {
                  if (entry.adminOnly && !isAdmin) return null;
                  return renderNavItem(entry);
                }
                // Group — filter admin-only items, skip group if empty
                const visibleInGroup = entry.items.filter(item => !item.adminOnly || isAdmin);
                if (visibleInGroup.length === 0) return null;
                const isGroupCollapsed = collapsedGroups.has(entry.id);
                const showItems = collapsed || !isGroupCollapsed;
                return (
                  <div key={entry.id} className="pt-2">
                    {collapsed ? (
                      idx > 0 && <div className="my-1 mx-2 border-t" style={{ borderColor: 'var(--ct-border)' }} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleGroup(entry.id)}
                        className="w-full flex items-center justify-between px-3 py-1 mb-1 hover:opacity-80 transition-opacity"
                        aria-expanded={!isGroupCollapsed}
                      >
                        <span className="ct-eyebrow">{t(entry.labelKey)}</span>
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform duration-150 ${isGroupCollapsed ? '-rotate-90' : ''}`}
                          style={{ color: 'var(--ct-text-disabled)' }}
                        />
                      </button>
                    )}
                    {showItems && visibleInGroup.map(renderNavItem)}
                  </div>
                );
              })}
            </nav>
          </TooltipProvider>
        </ScrollArea>

        {/* Bottom: User info + my account + theme toggle + logout */}
        <div className="border-t p-3 space-y-2" style={{ borderColor: 'var(--ct-border)' }}>
          {!collapsed && (
            <button
              type="button"
              data-testid="my-account-link"
              onClick={() => navigate('/my-account')}
              className="w-full flex items-start gap-2 text-left rounded px-2 py-1.5 transition-colors hover:opacity-80"
              style={{
                background: location.pathname === '/my-account' ? 'var(--ct-surface-active)' : 'transparent',
              }}
              title={t('menu.my-account')}
            >
              <UserCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--ct-accent)' }} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" style={{ color: 'var(--ct-text)' }}>{user?.firstName} {user?.lastName}</div>
                <div className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{user?.isAdmin ? t('roles.admin') : t('roles.lab')}</div>
              </div>
            </button>
          )}
          {collapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid="my-account-link"
                  onClick={() => navigate('/my-account')}
                  className="w-full flex items-center justify-center p-1.5 rounded transition-colors hover:opacity-80"
                  style={{
                    background: location.pathname === '/my-account' ? 'var(--ct-surface-active)' : 'transparent',
                    color: 'var(--ct-accent)',
                  }}
                >
                  <UserCircle className="w-5 h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">
                {t('menu.my-account')}
              </TooltipContent>
            </Tooltip>
          )}
          <div className="flex items-center gap-1">
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="flex items-center gap-2 text-xs p-1.5 rounded transition-colors"
              style={{ color: 'var(--ct-text-secondary)' }}
              title={theme === 'dark' ? t('sidebar.switch-to-light') : t('sidebar.switch-to-dark')}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {!collapsed && <span>{theme === 'dark' ? t('menu.theme-light') : t('menu.theme-dark')}</span>}
            </button>
            <div className="flex-1" />
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 text-xs p-1.5 rounded transition-colors"
              style={{ color: 'var(--ct-error)' }}
            >
              <LogOut className="w-4 h-4" />
              {!collapsed && <span>{t('menu.logout')}</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div
          className="h-14 flex items-center justify-between px-6 border-b flex-shrink-0"
          style={{ borderColor: 'var(--ct-border)', background: 'var(--ct-bg)' }}
        >
          {/* Breadcrumb: Sekce › Stránka (fallback jen „Přehled" pro root) */}
          <h2 className="flex items-center gap-1.5 text-sm" style={{ fontFamily: 'Inter' }}>
            {matchedGroup && (
              <>
                <span className="font-medium" style={{ color: 'var(--ct-text-disabled)' }}>
                  {t(matchedGroup.labelKey)}
                </span>
                <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--ct-text-disabled)' }} />
              </>
            )}
            <span className="font-semibold" style={{ color: 'var(--ct-text-secondary)' }}>
              {matchedItem ? t(matchedItem.labelKey) : t('menu.dashboard')}
            </span>
          </h2>
          <div className="flex items-center gap-3">
            {/* Trigger pro universal search */}
            <button
              type="button"
              data-testid="search-trigger"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded border text-xs transition-colors"
              style={{
                background: 'var(--ct-surface)',
                borderColor: 'var(--ct-border)',
                color: 'var(--ct-text-secondary)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ct-accent)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ct-border)'; }}
              title={t('topbar.search-tooltip')}
            >
              <Search className="w-3.5 h-3.5" />
              <span className="hidden md:inline">{t('topbar.search')}</span>
              <kbd
                className="hidden md:inline px-1 rounded border font-mono text-[10px]"
                style={{ background: 'var(--ct-bg-secondary)', borderColor: 'var(--ct-border)' }}
              >
                ⌘K
              </kbd>
            </button>

            <span className="text-xs font-mono" style={{ color: 'var(--ct-text-secondary)' }}>
              {new Date().toLocaleDateString(t('common.date-locale'))}
            </span>

            {/* Notification bell */}
            <div className="relative">
              <button
                data-testid="notification-bell"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-1.5 rounded transition-colors"
                style={{ color: errorCount > 0 ? 'var(--ct-error)' : warnCount > 0 ? 'var(--ct-warning)' : 'var(--ct-text-secondary)' }}
              >
                <Bell className="w-5 h-5" />
                {totalBadge > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ background: errorCount > 0 ? 'var(--ct-error)' : 'var(--ct-warning)' }}
                  >
                    {totalBadge}
                  </span>
                )}
              </button>

              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                  <div
                    className="absolute right-0 top-10 z-50 w-96 rounded-lg shadow-xl overflow-hidden"
                    style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)' }}
                  >
                    <div className="px-4 py-3 font-semibold text-sm" style={{ borderBottom: '1px solid var(--ct-border)', fontFamily: 'Inter', color: 'var(--ct-accent)' }}>
                      {t('topbar.notifications')} ({notifications.length})
                    </div>
                    <ScrollArea className="max-h-80">
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-sm" style={{ color: 'var(--ct-text-disabled)' }}>
                          {t('topbar.no-notifications')}
                        </div>
                      ) : (
                        notifications.map(n => (
                          <div key={n.id} className="ct-notification-item flex gap-3 group">
                            {getNotifIcon(n.type)}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium" style={{ color: 'var(--ct-text)' }}>{n.title}</div>
                              <div className="text-xs mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>{n.message}</div>
                            </div>
                            <button
                              data-testid="notification-delete"
                              onClick={(e) => { e.stopPropagation(); deleteNotification(n); }}
                              title={t('topbar.delete-notification')}
                              className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 self-start p-0.5 rounded hover:bg-black/10"
                              style={{ color: 'var(--ct-text-disabled)' }}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </ScrollArea>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4" style={{ background: 'var(--ct-bg)' }}>
          {children}
        </div>
      </div>

      {/* Chat bubble (hidden on /chat page) */}
      {location.pathname !== '/chat' && <ChatBubble />}

      {/* Universal search modal */}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
